import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { invoiceApi, poApi } from '../../utils/purchasingApi';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Purchasing.css';

const round2 = (n) => Math.round(n * 100) / 100;
const round4 = (n) => Math.round(n * 10000) / 10000;

function calcLine(l) {
  const qty      = parseFloat(l.quantity)  || 0;
  const price    = parseFloat(l.unitPrice) || 0;
  const taxRate  = parseFloat(l.taxRate)   ?? 14;
  const net      = round4(qty * price);
  const taxAmt   = round4(net * taxRate / 100);
  const total    = round4(net + taxAmt);
  return { ...l, taxAmount: taxAmt, lineTotal: total };
}

export default function PurchaseInvoiceFormPage() {
  const navigate = useNavigate();
  const { id }   = useParams(); // set when editing
  const isEdit   = !!id;

  const [loadingPage, setLoadingPage]   = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  // Form state
  const [poId, setPoId]                 = useState('');
  const [date, setDate]                 = useState(new Date().toISOString().slice(0, 10));
  const [vendorRef, setVendorRef]       = useState('');
  const [notes, setNotes]               = useState('');
  const [lines, setLines]               = useState([]);

  // Data
  const [receivedPos, setReceivedPos]   = useState([]);
  const [selectedPo, setSelectedPo]     = useState(null);
  const [accounts, setAccounts]         = useState([]);

  // Load received POs and accounts on mount
  useEffect(() => {
    async function init() {
      setLoadingPage(true);
      try {
        const [posData, acctData] = await Promise.all([
          poApi.list({ status: 'RECEIVED', limit: 200 }),
          api.get('/accounts?type=POSTING&isActive=true&limit=500'),
        ]);
        setReceivedPos(posData.items || []);

        setAccounts(acctData.items || acctData || []);

        if (isEdit) {
          const inv = await invoiceApi.getById(id);
          if (inv.status !== 'DRAFT') {
            navigate('/purchasing/invoices');
            return;
          }
          setPoId(inv.poId);
          setDate(inv.date ? inv.date.slice(0, 10) : '');
          setVendorRef(inv.vendorTaxInvoiceNumber || '');
          setNotes(inv.notes || '');
          setLines(inv.lines.map((l) => ({
            id:          l.id,
            lineNumber:  l.lineNumber,
            poLineId:    l.poLineId || '',
            description: l.description,
            quantity:    String(l.quantity),
            unit:        l.unit || '',
            unitPrice:   String(l.unitPrice),
            taxRate:     String(l.taxRate ?? 14),
            taxAmount:   Number(l.taxAmount),
            lineTotal:   Number(l.lineTotal),
            accountId:   l.accountId || '',
            notes:       l.notes || '',
          })));

          // Fetch the associated PO for context
          const po = await api.get(`/purchase-orders/${inv.poId}`);
          setSelectedPo(po);
        }
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoadingPage(false);
      }
    }
    init();
  }, [id, isEdit, navigate]);

  // When PO is selected (create mode), populate lines from PO
  const handlePoSelect = async (pid) => {
    setPoId(pid);
    setLines([]);
    setSelectedPo(null);
    if (!pid) return;
    try {
      const po = await api.get(`/purchase-orders/${pid}`);
      setSelectedPo(po);
      // Pre-populate invoice lines from PO lines
      setLines(po.lines.map((l, idx) => calcLine({
        lineNumber:  idx + 1,
        poLineId:    l.id,
        description: l.description,
        quantity:    String(l.quantity),
        unit:        l.unit || '',
        unitPrice:   String(l.unitPrice),
        taxRate:     String(l.taxRate ?? 14),
        taxAmount:   0,
        lineTotal:   0,
        accountId:   l.accountId || '',
        notes:       l.notes || '',
      })));
    } catch (e) {
      setError('Failed to load PO details: ' + (e.message || ''));
    }
  };

  const updateLine = (idx, field, value) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[idx] = calcLine({ ...updated[idx], [field]: value });
      return updated;
    });
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      calcLine({
        lineNumber:  prev.length + 1,
        poLineId:    '',
        description: '',
        quantity:    '1',
        unit:        '',
        unitPrice:   '0',
        taxRate:     '14',
        taxAmount:   0,
        lineTotal:   0,
        accountId:   '',
        notes:       '',
      }),
    ]);
  };

  const removeLine = (idx) => {
    setLines((prev) =>
      prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineNumber: i + 1 })),
    );
  };

  const totals = {
    subtotal: round2(lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0), 0)),
    tax:      round2(lines.reduce((s, l) => s + l.taxAmount, 0)),
    total:    round2(lines.reduce((s, l) => s + l.lineTotal, 0)),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!poId)         { setError('Please select a Purchase Order'); return; }
    if (!date)         { setError('Please enter an invoice date'); return; }
    if (lines.length === 0) { setError('Add at least one line'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        poId,
        date,
        vendorTaxInvoiceNumber: vendorRef || undefined,
        notes: notes || undefined,
        lines: lines.map((l, i) => ({
          lineNumber:  i + 1,
          poLineId:    l.poLineId || undefined,
          description: l.description,
          quantity:    parseFloat(l.quantity) || 0,
          unit:        l.unit || undefined,
          unitPrice:   parseFloat(l.unitPrice) || 0,
          taxRate:     parseFloat(l.taxRate) ?? 14,
          accountId:   l.accountId || undefined,
          notes:       l.notes || undefined,
        })),
      };

      if (isEdit) {
        await invoiceApi.update(id, payload);
      } else {
        await invoiceApi.create(payload);
      }
      navigate('/purchasing/invoices');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loadingPage) return <LoadingSpinner variant="fullpage" />;

  return (
    <div className="purch-form-page">
      <div className="purch-form-page__header">
        <button className="purch-back-btn" onClick={() => navigate('/purchasing/invoices')}>
          <i className="fas fa-arrow-left" /> Back
        </button>
        <h1 className="purch-title">{isEdit ? 'Edit Purchase Invoice' : 'New Purchase Invoice'}</h1>
      </div>

      {error && <div className="purch-alert purch-alert--error">{error}</div>}

      <form className="purch-form" onSubmit={handleSubmit}>
        {/* Header fields */}
        <div className="purch-form__section">
          <h2 className="purch-form__section-title">Invoice Details</h2>
          <div className="purch-form__grid">
            <div className="purch-form__field purch-form__field--wide">
              <label>Purchase Order (RECEIVED) *</label>
              <select
                className="purch-select"
                value={poId}
                onChange={(e) => handlePoSelect(e.target.value)}
                disabled={isEdit}
                required
              >
                <option value="">— Select a PO —</option>
                {receivedPos.map((po) => (
                  <option key={po.id} value={po.id}>
                    PO-{po.poNumber} | {po.vendor?.name} | {po.currency?.code} {Number(po.totalAmount).toLocaleString()}
                  </option>
                ))}
              </select>
              {isEdit && <small className="purch-hint">PO cannot be changed on an existing invoice</small>}
            </div>

            {selectedPo && (
              <div className="purch-po-summary">
                <span><strong>Vendor:</strong> {selectedPo.vendor?.name}</span>
                <span><strong>Entity:</strong> {selectedPo.entity?.name || '—'}</span>
                <span><strong>Currency:</strong> {selectedPo.currency?.code}</span>
                <span><strong>PO Total:</strong> {Number(selectedPo.totalAmount).toLocaleString()}</span>
              </div>
            )}

            <div className="purch-form__field">
              <label>Invoice Date *</label>
              <input type="date" className="purch-input" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>

            <div className="purch-form__field">
              <label>Vendor Tax Invoice #</label>
              <input
                type="text"
                className="purch-input"
                placeholder="Vendor's own invoice reference"
                value={vendorRef}
                onChange={(e) => setVendorRef(e.target.value)}
                maxLength={64}
              />
            </div>

            <div className="purch-form__field purch-form__field--wide">
              <label>Notes</label>
              <textarea
                className="purch-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={500}
              />
            </div>
          </div>
        </div>

        {/* Lines */}
        <div className="purch-form__section">
          <div className="purch-form__section-header">
            <h2 className="purch-form__section-title">Invoice Lines</h2>
            <button type="button" className="purch-btn purch-btn--sm purch-btn--outline" onClick={addLine}>
              <i className="fas fa-plus" /> Add Line
            </button>
          </div>

          {lines.length === 0 ? (
            <p className="purch-hint">Select a PO above to auto-populate lines, or add lines manually.</p>
          ) : (
            <div className="purch-lines-table-wrap">
              <table className="purch-table purch-lines-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description *</th>
                    <th>Qty *</th>
                    <th>Unit</th>
                    <th>Unit Price *</th>
                    <th>Tax %</th>
                    <th>Tax Amt</th>
                    <th>Line Total</th>
                    <th>Expense Account *</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => (
                    <tr key={idx}>
                      <td>{l.lineNumber}</td>
                      <td>
                        <input
                          className="purch-input purch-input--inline"
                          value={l.description}
                          onChange={(e) => updateLine(idx, 'description', e.target.value)}
                          required
                          maxLength={255}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="purch-input purch-input--num"
                          value={l.quantity}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                          min="0.0001"
                          step="any"
                          required
                        />
                      </td>
                      <td>
                        <input
                          className="purch-input purch-input--unit"
                          value={l.unit}
                          onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                          maxLength={32}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="purch-input purch-input--num"
                          value={l.unitPrice}
                          onChange={(e) => updateLine(idx, 'unitPrice', e.target.value)}
                          min="0"
                          step="any"
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="purch-input purch-input--num purch-input--short"
                          value={l.taxRate}
                          onChange={(e) => updateLine(idx, 'taxRate', e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </td>
                      <td className="purch-table__amount purch-table__readonly">
                        {l.taxAmount.toLocaleString('en-EG', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="purch-table__amount purch-table__readonly">
                        <strong>{l.lineTotal.toLocaleString('en-EG', { minimumFractionDigits: 2 })}</strong>
                      </td>
                      <td>
                        <select
                          className="purch-select purch-select--inline"
                          value={l.accountId}
                          onChange={(e) => updateLine(idx, 'accountId', e.target.value)}
                        >
                          <option value="">— Select account —</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="purch-btn purch-btn--icon purch-btn--danger"
                          onClick={() => removeLine(idx)}
                          title="Remove line"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6} className="purch-table__amount"><strong>Subtotal (excl. tax):</strong></td>
                    <td colSpan={2} className="purch-table__amount">{totals.subtotal.toLocaleString('en-EG', { minimumFractionDigits: 2 })}</td>
                    <td colSpan={2} />
                  </tr>
                  <tr>
                    <td colSpan={6} className="purch-table__amount"><strong>VAT:</strong></td>
                    <td colSpan={2} className="purch-table__amount">{totals.tax.toLocaleString('en-EG', { minimumFractionDigits: 2 })}</td>
                    <td colSpan={2} />
                  </tr>
                  <tr className="purch-totals-row">
                    <td colSpan={6} className="purch-table__amount"><strong>Total:</strong></td>
                    <td colSpan={2} className="purch-table__amount">
                      <strong>{totals.total.toLocaleString('en-EG', { minimumFractionDigits: 2 })}</strong>
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Submit buttons */}
        <div className="purch-form__actions">
          <button type="submit" className="purch-btn purch-btn--primary" disabled={saving}>
            {saving ? <LoadingSpinner variant="button" /> : (isEdit ? 'Save Changes' : 'Create Invoice')}
          </button>
          <button type="button" className="purch-btn purch-btn--outline" onClick={() => navigate('/purchasing/invoices')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
